import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, User, LogOut, MessageSquare, LogIn, Mail } from 'lucide-react';
import { isAuthenticated, logout, getUser } from '../../utils/auth';
import { Button } from '../ui/button';
import Dropdown from '../ui/Dropdown';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const user = getUser();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Services Dropdown Items
  const servicesItems = [
    {
      title: i18n.language === 'ar' ? 'جميع خدماتنا القانونية' : 'All Our Legal Services',
      description: i18n.language === 'ar' ? 'استعرض جميع الـ 18 خدمة قانونية' : 'Browse all 18 legal services',
      link: '/services'
    },
    {
      title: i18n.language === 'ar' ? 'الدوائر المتخصصة' : 'Specialized Departments',
      description: i18n.language === 'ar' ? 'استعرض الدوائر القانونية المتخصصة' : 'Browse specialized legal departments',
      link: '/services/overview'
    },
    {
      title: i18n.language === 'ar' ? 'التقاضي' : 'Litigation',
      description: i18n.language === 'ar' ? 'تمثيل قانوني أمام المحاكم' : 'Legal representation in courts',
      link: '/services/litigation'
    },
    {
      title: i18n.language === 'ar' ? 'العقود' : 'Contracts',
      description: i18n.language === 'ar' ? 'صياغة ومراجعة العقود' : 'Contract drafting & review',
      link: '/services/contracts'
    },
    {
      title: i18n.language === 'ar' ? 'الاستشارات' : 'Consultation',
      description: i18n.language === 'ar' ? 'استشارات قانونية متخصصة' : 'Specialized legal consultation',
      link: '/services/consultation'
    }
  ];

  // Packages Dropdown Items
  const packagesItems = [
    {
      title: i18n.language === 'ar' ? 'وازن بيسك' : 'Wazin Basic',
      description: i18n.language === 'ar' ? 'باقة أساسية للشركات الصغيرة' : 'Basic package for small businesses',
      link: '/packages#basic'
    },
    {
      title: i18n.language === 'ar' ? 'وازن بريميوم' : 'Wazin Premium',
      description: i18n.language === 'ar' ? 'باقة متقدمة للشركات المتوسطة' : 'Advanced package for medium businesses',
      link: '/packages#premium'
    },
    {
      title: i18n.language === 'ar' ? 'وازن إيليت' : 'Wazin Elite',
      description: i18n.language === 'ar' ? 'باقة شاملة للشركات الكبرى' : 'Comprehensive package for large corporations',
      link: '/packages#elite'
    },
    {
      title: i18n.language === 'ar' ? 'مقارنة الباقات' : 'Compare Packages',
      description: i18n.language === 'ar' ? 'قارن بين جميع الباقات' : 'Compare all packages',
      link: '/packages'
    }
  ];

  // Legal Divisions Dropdown Items
  const divisionsItems = [
    {
      title: i18n.language === 'ar' ? 'دائرة التقاضي' : 'Litigation Division',
      description: i18n.language === 'ar' ? 'تمثيل قانوني أمام المحاكم' : 'Legal representation in courts',
      link: '/divisions/litigation'
    },
    {
      title: i18n.language === 'ar' ? 'دائرة العقود والشركات' : 'Contracts & Corporate Division',
      description: i18n.language === 'ar' ? 'صياغة العقود وخدمات الشركات' : 'Contract drafting and corporate services',
      link: '/divisions/contracts-and-corporate'
    },
    {
      title: i18n.language === 'ar' ? 'دائرة الدراسات والاستشارات' : 'Studies & Consultations Division',
      description: i18n.language === 'ar' ? 'استشارات قانونية متخصصة' : 'Specialized legal consultations',
      link: '/divisions/consultations'
    },
    {
      title: i18n.language === 'ar' ? 'دائرة التوثيق' : 'Notary Division',
      description: i18n.language === 'ar' ? 'خدمة التوثيق المعتمدة' : 'Certified notary services',
      link: '/divisions/notary'
    },
    {
      title: i18n.language === 'ar' ? 'جميع الدوائر' : 'All Divisions',
      description: i18n.language === 'ar' ? 'استعرض كافة الدوائر' : 'Browse all divisions',
      link: '/divisions'
    }
  ];

  return (
    <nav className="fixed top-0 w-full z-50 glass border-b border-white/20 shadow-lg backdrop-blur-md bg-white border-t-2 border-t-[#C8A870]/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-36 py-4 gap-6" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
          
          {/* Logo - Left Side */}
          <Link to="/" className="flex items-center shrink-0 pr-6" data-testid="logo-link">
            <img 
              src="/images/wazin-logo.png" 
              alt="Wazin Logo" 
              className="h-24 w-auto object-contain"
              style={{ maxWidth: '240px' }}
            />
          </Link>

          {/* Desktop Navigation - Center Flex */}
          <div className="hidden md:flex items-center gap-3 flex-1">
            
            {/* Navigation Links */}
            {!isAuthenticated() ? (
              <div className="flex items-center gap-3">
                <Link to="/" className="text-gray-700 hover:text-[#C8A870] font-medium transition-all duration-300 ease-out px-1.5 py-2 relative group whitespace-nowrap text-sm" data-testid="nav-home">
                  {i18n.language === 'ar' ? 'الرئيسية' : 'Home'}
                  <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C8A870] group-hover:w-full transition-all duration-300 ease-out"></span>
                </Link>
                
                {/* Services Dropdown */}
                <Dropdown 
                  label={i18n.language === 'ar' ? 'الخدمات' : 'Services'} 
                  items={servicesItems}
                />
                
                {/* Legal Divisions Dropdown */}
                <Dropdown 
                  label={i18n.language === 'ar' ? 'الدوائر القانونية' : 'Legal Divisions'} 
                  items={divisionsItems}
                />
                
                {/* Packages Dropdown */}
                <Dropdown 
                  label={i18n.language === 'ar' ? 'الباقات' : 'Packages'} 
                  items={packagesItems}
                />
                
                <Link to="/lawyers" className="text-gray-700 hover:text-[#C8A870] font-medium transition-all duration-300 ease-out px-1.5 py-2 relative group whitespace-nowrap text-sm">
                  {i18n.language === 'ar' ? 'المحامون' : 'Lawyers'}
                  <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C8A870] group-hover:w-full transition-all duration-300 ease-out"></span>
                </Link>
                <Link to="/about" className="text-gray-700 hover:text-[#C8A870] font-medium transition-all duration-300 ease-out px-1.5 py-2 relative group whitespace-nowrap text-sm">
                  {i18n.language === 'ar' ? 'من نحن' : 'About Us'}
                  <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C8A870] group-hover:w-full transition-all duration-300 ease-out"></span>
                </Link>
                <Link to="/#contact" className="text-gray-700 hover:text-[#C8A870] font-medium transition-all duration-300 ease-out px-1.5 py-2 relative group whitespace-nowrap text-sm" data-testid="nav-contact">
                  {i18n.language === 'ar' ? 'اتصل بنا' : 'Contact'}
                  <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C8A870] group-hover:w-full transition-all duration-300 ease-out"></span>
                </Link>
              </div>
            ) : (
              <div className="flex items-center gap-6">
                <Link to="/dashboard" className="text-gray-700 hover:text-wazin-gold font-medium whitespace-nowrap" data-testid="nav-dashboard">
                  {t('dashboard')}
                </Link>
                {user?.role === 'admin' && (
                  <>
                    <Link to="/admin/cases-advanced" className="text-gray-700 hover:text-wazin-gold font-medium whitespace-nowrap">
                      {i18n.language === 'ar' ? 'القضايا' : 'Cases'}
                    </Link>
                    <Link to="/hr/employees" className="text-gray-700 hover:text-wazin-gold font-medium whitespace-nowrap">
                      {i18n.language === 'ar' ? 'الموظفين' : 'Employees'}
                    </Link>
                    <Link to="/hr/requests" className="text-gray-700 hover:text-wazin-gold font-medium whitespace-nowrap">
                      {i18n.language === 'ar' ? 'الطلبات' : 'Requests'}
                    </Link>
                  </>
                )}
                <Link to="/tickets" className="text-gray-700 hover:text-wazin-gold font-medium whitespace-nowrap">
                  {i18n.language === 'ar' ? 'التذاكر' : 'Tickets'}
                </Link>
                <Link to="/chat" className="text-gray-700 hover:text-wazin-gold font-medium flex items-center gap-2 whitespace-nowrap" data-testid="nav-chat">
                  <MessageSquare className="w-4 h-4" />
                  {i18n.language === 'ar' ? 'المحادثات' : 'Chat'}
                </Link>
              </div>
            )}
          </div>

          {/* Right Side - Icons & Buttons */}
          <div className="hidden md:flex items-center gap-3 shrink-0">
            {/* Email Button */}
            <a
              href="mailto:info@wazin.sa"
              className="flex items-center justify-center w-10 h-10 rounded-full bg-[#C8A870]/10 border-2 border-[#C8A870] hover:bg-[#C8A870] hover:text-wazin-navy text-[#C8A870] transition-all duration-300 shadow-sm hover:shadow-md"
              title={i18n.language === 'ar' ? 'راسلنا' : 'Email Us'}
            >
              <Mail className="w-[18px] h-[18px]" />
            </a>

            {/* Language Switcher - Flag Icons */}
            <button
              onClick={toggleLanguage}
              className="flex items-center justify-center w-12 h-10 rounded-full border-2 border-[#C8A870] hover:bg-[#C8A870]/20 hover:scale-105 transition-all duration-300 shadow-sm hover:shadow-md bg-white overflow-hidden"
              data-testid="language-toggle"
              title={i18n.language === 'ar' ? 'التبديل إلى الإنجليزية' : 'Switch to Arabic'}
            >
              {i18n.language === 'ar' ? (
                <img 
                  src="https://flagcdn.com/w40/sa.png" 
                  alt="Saudi Arabia Flag"
                  className="w-8 h-6 object-cover rounded"
                />
              ) : (
                <img 
                  src="https://flagcdn.com/w40/gb.png" 
                  alt="UK Flag"
                  className="w-8 h-6 object-cover rounded"
                />
              )}
            </button>

            {/* New Login/Register Buttons */}
            {!isAuthenticated() ? (
              <div className="flex items-center gap-3 ml-3">
                {/* Login Button */}
                <Button
                  onClick={() => navigate('/select-user-type')}
                  className="h-10 px-6 bg-gradient-to-r from-wazin-gold to-yellow-600 hover:from-wazin-gold/90 hover:to-yellow-600/90 text-wazin-navy font-bold transition-all duration-300 flex items-center justify-center gap-2 rounded-lg shadow-md hover:shadow-lg text-sm whitespace-nowrap"
                  data-testid="nav-login-btn"
                >
                  <LogIn className="w-4 h-4" />
                  {i18n.language === 'ar' ? 'تسجيل الدخول' : 'Login'}
                </Button>
                
                {/* Register Button */}
                <Button
                  onClick={() => navigate('/register')}
                  className="h-10 px-6 bg-white text-wazin-navy hover:bg-gray-50 font-bold transition-all duration-300 flex items-center justify-center gap-2 rounded-lg shadow-md hover:shadow-lg border-2 border-wazin-navy text-sm whitespace-nowrap"
                  size="sm"
                >
                  <User className="w-4 h-4" />
                  {i18n.language === 'ar' ? 'إنشاء حساب' : 'Register'}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/profile" data-testid="nav-profile">
                  <Button variant="outline" size="sm" className="gap-2">
                    <User className="w-4 h-4" />
                    {user?.full_name}
                  </Button>
                </Link>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  {t('logout')}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-3 rounded-lg bg-wazin-gold/10 border-2 border-wazin-gold hover:bg-wazin-gold hover:text-wazin-navy transition-all"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-7 h-7 text-wazin-gold" /> : <Menu className="w-7 h-7 text-wazin-gold" />}
          </button>
        </div>

        {/* Mobile Sidebar Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 top-40 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
            <div 
              className={`fixed top-40 ${i18n.language === 'ar' ? 'right-0' : 'left-0'} h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 overflow-y-auto`}
              onClick={(e) => e.stopPropagation()}
              dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
            >
              <div className="p-6 space-y-4">
                {/* Navigation Links */}
                {!isAuthenticated() ? (
                  <>
                    <Link 
                      to="/" 
                      className="block px-4 py-3 text-lg font-medium text-gray-700 hover:bg-wazin-gold/10 hover:text-wazin-gold rounded-lg transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {i18n.language === 'ar' ? 'الرئيسية' : 'Home'}
                    </Link>
                    <Link 
                      to="/services" 
                      className="block px-4 py-3 text-lg font-medium text-gray-700 hover:bg-wazin-gold/10 hover:text-wazin-gold rounded-lg transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {i18n.language === 'ar' ? 'الخدمات' : 'Services'}
                    </Link>
                    <Link 
                      to="/services/all" 
                      className="block px-4 py-3 text-lg font-medium text-gray-700 hover:bg-wazin-gold/10 hover:text-wazin-gold rounded-lg transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {i18n.language === 'ar' ? 'الدوائر القانونية' : 'Legal Departments'}
                    </Link>
                    <Link 
                      to="/packages" 
                      className="block px-4 py-3 text-lg font-medium text-gray-700 hover:bg-wazin-gold/10 hover:text-wazin-gold rounded-lg transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {i18n.language === 'ar' ? 'الباقات' : 'Packages'}
                    </Link>
                    <Link 
                      to="/lawyers" 
                      className="block px-4 py-3 text-lg font-medium text-gray-700 hover:bg-wazin-gold/10 hover:text-wazin-gold rounded-lg transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {i18n.language === 'ar' ? 'المحامون' : 'Lawyers'}
                    </Link>
                    <Link 
                      to="/about" 
                      className="block px-4 py-3 text-lg font-medium text-gray-700 hover:bg-wazin-gold/10 hover:text-wazin-gold rounded-lg transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {i18n.language === 'ar' ? 'من نحن' : 'About Us'}
                    </Link>
                    <Link 
                      to="/#contact" 
                      className="block px-4 py-3 text-lg font-medium text-gray-700 hover:bg-wazin-gold/10 hover:text-wazin-gold rounded-lg transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {i18n.language === 'ar' ? 'اتصل بنا' : 'Contact'}
                    </Link>

                    {/* Divider */}
                    <div className="border-t border-gray-200 my-6"></div>

                    {/* Auth Buttons */}
                    <div className="space-y-3">
                      <Button
                        onClick={() => {
                          navigate('/admin/dashboard');
                          setMobileMenuOpen(false);
                        }}
                        className="w-full h-12 bg-wazin-navy text-white hover:bg-wazin-navy/90 font-semibold flex items-center justify-center gap-2.5 rounded-lg"
                      >
                        <Briefcase className="w-5 h-5" />
                        {i18n.language === 'ar' ? 'بوابة الموظفين' : 'Employee Panel'}
                      </Button>
                      <Button
                        onClick={() => {
                          navigate('/client/dashboard');
                          setMobileMenuOpen(false);
                        }}
                        className="w-full h-12 bg-wazin-gold text-wazin-navy hover:bg-wazin-gold/90 font-semibold flex items-center justify-center gap-2.5 rounded-lg"
                      >
                        <Users className="w-5 h-5" />
                        {i18n.language === 'ar' ? 'بوابة العميل' : 'Client Portal'}
                      </Button>
                      <Button
                        onClick={() => {
                          navigate('/login');
                          setMobileMenuOpen(false);
                        }}
                        className="w-full h-12 bg-white text-gray-700 hover:bg-gray-50 font-semibold flex items-center justify-center gap-2.5 rounded-lg border-2 border-gray-300"
                      >
                        <LogIn className="w-5 h-5" />
                        {i18n.language === 'ar' ? 'تسجيل الدخول' : 'Login'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Link to="/dashboard" className="block px-4 py-3 text-lg font-medium text-gray-700 hover:bg-wazin-gold/10 hover:text-wazin-gold rounded-lg transition-all" onClick={() => setMobileMenuOpen(false)}>
                      {t('dashboard')}
                    </Link>
                    {user?.role === 'admin' && (
                      <>
                        <Link to="/admin/cases-advanced" className="block px-4 py-3 text-lg font-medium text-gray-700 hover:bg-wazin-gold/10 hover:text-wazin-gold rounded-lg transition-all" onClick={() => setMobileMenuOpen(false)}>
                          {i18n.language === 'ar' ? 'القضايا' : 'Cases'}
                        </Link>
                        <Link to="/hr/employees" className="block px-4 py-3 text-lg font-medium text-gray-700 hover:bg-wazin-gold/10 hover:text-wazin-gold rounded-lg transition-all" onClick={() => setMobileMenuOpen(false)}>
                          {i18n.language === 'ar' ? 'الموظفين' : 'Employees'}
                        </Link>
                      </>
                    )}
                    <Button onClick={handleLogout} className="w-full mt-4" variant="outline">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('logout')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-3" data-testid="mobile-menu">
            {!isAuthenticated() ? (
              <>
                <Link to="/" className="block py-2 text-text-primary hover:text-primary">
                  {t('home')}
                </Link>
                <Link to="/services" className="block py-2 text-text-primary hover:text-primary">
                  {i18n.language === 'ar' ? 'خدماتنا' : 'Our Services'}
                </Link>
                <Link to="/#departments" className="block py-2 text-text-primary hover:text-primary">
                  {t('departments')}
                </Link>
                <Link to="/#contact" className="block py-2 text-text-primary hover:text-primary">
                  {t('contact')}
                </Link>
                <div className="flex flex-col gap-2 pt-2">
                  <Button onClick={() => navigate('/login')} variant="outline" className="w-full">
                    {t('login')}
                  </Button>
                  <Button onClick={() => navigate('/register')} className="btn-primary w-full">
                    {t('register')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Link to="/dashboard" className="block py-2 text-text-primary hover:text-primary">
                  {t('dashboard')}
                </Link>
                <Link to="/cases" className="block py-2 text-text-primary hover:text-primary">
                  {t('myCases')}
                </Link>
                <Link to="/profile" className="block py-2 text-text-primary hover:text-primary">
                  {t('profile')}
                </Link>
                <Button onClick={handleLogout} variant="outline" className="w-full">
                  {t('logout')}
                </Button>
              </>
            )}
            <Button onClick={toggleLanguage} variant="outline" className="w-full">
              {i18n.language === 'ar' ? 'English' : 'العربية'}
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
